import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  useAccount,
  useConnect,
  useSignMessage,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWriteContract
} from 'wagmi'

import ConfirmStakingPopup from '../../components/staking/ConfirmStakingPopup'
import ConfirmSymbolClaimPopup from '../../components/staking/ConfirmSymbolClaimPopup'
import Staking from '../../components/staking/Staking'
import SuccessPopup from '../../components/staking/SuccessPopup'
import {
  DEFAULT_CONTAINER_STATE,
  STAKING_PROCESS_STEP
} from '../../constants/containerStates'
import {
  changeStakingState,
  claimStakingReward,
  claimSymbolReward,
  getStakingReward,
  getSymbolReward,
  getUserTier,
  resetStakingState
} from '../../store/actions/staking'
import { getWallet } from '../../store/actions/wallet'
import { STAKING_PROFILE_DISPATCH } from '../../store/reducers/staking/staking'
import {
  selectMainProfile,
  selectStakingProfile,
  selectTokenProfile,
  selectWalletProfile
} from '../../store/selectors/common'
import { getWalletAddressSelector } from '../../store/selectors/wallet'
import { floorNumber, formatSCAmount } from '../../util/number.helpers'
import { buildTransactionErrorMessage } from '../../util/rewardBox.helpers'
import {
  CURRENCIES,
  formatQBXCurrency,
  getTxnLog,
  stakingContractConfig,
  zkContractConfig
} from '../../util/staking/staking.helpers'
import { formatTicks } from '../../util/string.helpers'
import { getChain } from '../../util/wallet/wagmiConfig'

const chainId = getChain().id

const stakingConfig = stakingContractConfig()
const zkConfig = zkContractConfig()

const StakingContainer = ({ walletConnectProps }) => {
  const { renderWalletPopup, stakingContractResponse, openConnectModal } =
    walletConnectProps

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { isConnected } = useAccount()
  const { connect, connectors } = useConnect()

  useEffect(() => {
    const lastConnector = localStorage.getItem('wagmi.connectedConnector')

    if (!isConnected && lastConnector && connectors.length > 0) {
      const connector = connectors.find((c) => c.id === lastConnector)
      if (connector) {
        connect({ connector })
      }
    }
  }, [isConnected, connect, connectors])

  const walletAddressMembership = useSelector(getWalletAddressSelector)
  const mainProfile = useSelector(selectMainProfile)
  const tokenProfile = useSelector(selectTokenProfile)
  const stakingProfile = useSelector(selectStakingProfile)
  const connectedWallet = walletAddressMembership.membershipNumber

  const [popupState, setPopupState] = useState({
    id: DEFAULT_CONTAINER_STATE.NONE,
    data: null
  })

  const walletProfile = useSelector(selectWalletProfile)

  useEffect(() => {
    if (connectedWallet) {
      dispatch(getWallet(connectedWallet))
      dispatch(getStakingReward(connectedWallet))
      dispatch(getSymbolReward())
    }
    return () => {
      dispatch(resetStakingState())
    }
  }, [connectedWallet])

  const { switchChain } = useSwitchChain()

  // UnStake / Claim QBX
  const {
    data: unStakeHash,
    isPending: unStakePending,
    error: unStakeErr,
    writeContract: unStakeWrite
  } = useWriteContract()

  const {
    isLoading: unStakeProcessing,
    isSuccess: unStakeConfirmed,
    data: unStakeReceiptData
  } = useWaitForTransactionReceipt({
    hash: unStakeHash
  })

  useEffect(() => {
    if (unStakeErr) {
      updateError({
        unStake: buildTransactionErrorMessage(unStakeErr.shortMessage)
      })
    }
  }, [unStakeErr])

  useEffect(() => {
    if (unStakeProcessing || unStakePending) {
      updateUnStakeProcessing(true)
    }
  }, [unStakeProcessing, unStakePending])

  useEffect(() => {
    if (unStakeConfirmed) {
      let claimedAmount = 0
      try {
        const log = getTxnLog(unStakeReceiptData?.logs, stakingConfig.address)
        if (log) {
          claimedAmount = formatSCAmount(log.data)
        }
      } catch (e) {
        console.log(e)
      }
      updateError({})
      updateUnStakeProcessing(false)
      setPopupState({
        id: STAKING_PROCESS_STEP.SUCCESS,
        data: {
          type: STAKING_PROCESS_STEP.UN_STAKE,
          value: formatQBXCurrency(claimedAmount)
        }
      })
      stakingContractResponse.refetch()
    }
  }, [unStakeConfirmed])

  // Claim Staking Reward
  const {
    signMessage,
    error: signMsgErr,
    data: signMsgData,
    isPending: signMsgPending
  } = useSignMessage()

  useEffect(() => {
    if (signMsgPending) {
      updateRewardClaimProcessing(true)
    }
  }, [signMsgPending])

  useEffect(() => {
    if (signMsgErr) {
      updateError({
        rewardClaim: buildTransactionErrorMessage(signMsgErr.shortMessage)
      })
    }
  }, [signMsgErr])

  useEffect(() => {
    if (signMsgData) {
      dispatch(claimStakingReward({ address: connectedWallet, signMsgData }))
    }
  }, [signMsgData])

  const {
    data: stakingRewardClaimHash,
    isPending: stakingRewardClaimPending,
    error: stakingRewardClaimErr,
    writeContract: stakingRewardClaimWrite
  } = useWriteContract()

  const {
    isLoading: stakingRewardClaimProcessing,
    isSuccess: stakingRewardClaimConfirmed,
    data: stakingRewardReceipt
  } = useWaitForTransactionReceipt({
    hash: stakingRewardClaimHash
  })

  useEffect(() => {
    if (stakingRewardClaimConfirmed) {
      let claimedAmount = 0
      try {
        const log = getTxnLog(stakingRewardReceipt?.logs, zkConfig.address)
        if (log) {
          claimedAmount = formatSCAmount(log.data)
        }
      } catch (e) {
        console.log(e)
      }
      dispatchAction(STAKING_PROFILE_DISPATCH.STAKING_REWARD, 0)
      updateRewardClaimProcessing(false)
      updateError({})
      setPopupState({
        id: STAKING_PROCESS_STEP.SUCCESS,
        data: {
          type: STAKING_PROCESS_STEP.REWARD_CLAIM,
          value: formatQBXCurrency(claimedAmount)
        }
      })
    }
  }, [stakingRewardClaimConfirmed])

  useEffect(() => {
    if (stakingRewardClaimErr) {
      updateError({
        rewardClaim: buildTransactionErrorMessage(
          stakingRewardClaimErr.shortMessage
        )
      })
    }
  }, [stakingRewardClaimErr])

  useEffect(() => {
    if (stakingRewardClaimProcessing || stakingRewardClaimPending) {
      updateRewardClaimProcessing(true)
    }
  }, [stakingRewardClaimProcessing, stakingRewardClaimPending])

  useEffect(() => {
    if (stakingProfile.rewardStakingPayload?.releaseable) {
      const { nonce, signedData, validUntil, releaseable } =
        stakingProfile.rewardStakingPayload
      if (signedData && releaseable) {
        switchChain({ chainId })
        updateRewardClaimProcessing(true)
        stakingRewardClaimWrite({
          ...zkConfig,
          functionName: 'transferTokens',
          args: [releaseable, nonce, validUntil, signedData]
        })
      }
    }
  }, [stakingProfile.rewardStakingPayload])

  const [actionType, setActionType] = useState(null)

  useEffect(() => {
    if (isConnected && actionType) {
      if (actionType === STAKING_PROCESS_STEP.UN_STAKE) {
        executeUnStake()
      } else if (actionType === STAKING_PROCESS_STEP.REWARD_CLAIM) {
        executeRewardClaim()
      }
    }
  }, [isConnected])

  const stakingSymbolReward = useMemo(() => {
    const { stakingReward, priceOfQBX } = stakingProfile
    return floorNumber(stakingReward * (priceOfQBX / tokenProfile.onramp))
  }, [
    stakingProfile.stakingReward,
    stakingProfile.priceOfQBX,
    tokenProfile.onramp
  ])

  useEffect(() => {
    if (stakingProfile.symbolRewardClaimed) {
      setPopupState({
        id: STAKING_PROCESS_STEP.SUCCESS,
        data: {
          type: STAKING_PROCESS_STEP.SYMBOL_REWARD_CLAIM,
          value: `${formatTicks(stakingProfile.symbolRewardClaimed)} ${tokenProfile.symbol}`
        }
      })
    }
  }, [stakingProfile.symbolRewardClaimed])

  const updateUnStakeProcessing = (isProcessing) => {
    dispatchAction(STAKING_PROFILE_DISPATCH.UN_STAKE_PROCESSING, isProcessing)
  }

  const updateRewardClaimProcessing = (isProcessing) => {
    dispatchAction(
      STAKING_PROFILE_DISPATCH.REWARD_CLAIM_PROCESSING,
      isProcessing
    )
  }

  const updateError = (error) => {
    dispatchAction(STAKING_PROFILE_DISPATCH.ERROR, error)
  }

  const dispatchAction = (property, value) => {
    dispatch(changeStakingState(property, value))
  }

  const handleNoPopup = () => {
    setPopupState({ data: null, id: DEFAULT_CONTAINER_STATE.NONE })
    updateError({})
  }

  const handleRefreshUserTier = () => {
    dispatch(getUserTier())
  }

  const executeUnStake = () => {
    switchChain({ chainId })
    updateUnStakeProcessing(true)
    unStakeWrite({
      ...stakingConfig,
      functionName: 'withdraw',
      args: []
    })
  }

  const executeRewardClaim = () => {
    updateRewardClaimProcessing(true)
    signMessage({ message: CURRENCIES.QBX })
  }
  const handlePopupSubmit = () => {
    switch (popupState.id) {
      case STAKING_PROCESS_STEP.UN_STAKE:
        if (isConnected) {
          setActionType(null)
          executeUnStake()
        } else {
          setActionType(popupState.id)
          openConnectModal()
        }
        break

      case STAKING_PROCESS_STEP.REWARD_CLAIM:
        if (isConnected) {
          setActionType(null)
          executeRewardClaim()
        } else {
          setActionType(popupState.id)
          openConnectModal()
        }
        break

      case STAKING_PROCESS_STEP.SYMBOL_REWARD_CLAIM:
        dispatch(claimSymbolReward())
        break

      default:
        handleNoPopup()
        break
    }
  }

  const renderPopup = () => {
    switch (popupState.id) {
      case STAKING_PROCESS_STEP.UN_STAKE:
        return (
          <ConfirmStakingPopup
            title='brand:staking.claim-staked-title'
            desc='brand:staking.claim-staked-desc'
            submitLabel='brand:staking.claim-staked'
            onSubmit={handlePopupSubmit}
            onCancel={handleNoPopup}
            value={stakingProfile.stakedQBX}
            loading={stakingProfile.unStakeProcessing}
            error={stakingProfile.error[STAKING_PROCESS_STEP.UN_STAKE]}
            t={t}
            disableClose
          />
        )

      case STAKING_PROCESS_STEP.REWARD_CLAIM:
        return (
          <ConfirmStakingPopup
            title='brand:staking.claim-staking-reward-title'
            desc='brand:staking.claim-staking-reward-desc'
            submitLabel='brand:staking.claim-qbx-reward'
            onSubmit={handlePopupSubmit}
            onCancel={handleNoPopup}
            value={stakingProfile.stakingReward}
            loading={stakingProfile.rewardClaimProcessing}
            error={stakingProfile.error[STAKING_PROCESS_STEP.REWARD_CLAIM]}
            t={t}
            disableClose
            stakingValue={`${formatTicks(stakingSymbolReward)} ${tokenProfile.symbol}`}
          />
        )

      case STAKING_PROCESS_STEP.SYMBOL_REWARD_CLAIM:
        return (
          <ConfirmSymbolClaimPopup
            onSubmit={handlePopupSubmit}
            onCancel={handleNoPopup}
            value={stakingSymbolReward}
            loading={stakingProfile.symbolRewardClaimProcessing}
            error={
              stakingProfile.error[STAKING_PROCESS_STEP.SYMBOL_REWARD_CLAIM]
            }
            symbol={tokenProfile.symbol}
            t={t}
            stakingValue={formatQBXCurrency(stakingProfile.stakingReward)}
            disableClose
          />
        )

      case STAKING_PROCESS_STEP.SUCCESS:
        return (
          <SuccessPopup
            onSubmit={handlePopupSubmit}
            onCancel={handleNoPopup}
            t={t}
            symbol={tokenProfile.symbol}
            {...popupState?.data}
          />
        )

      default:
        break
    }
  }

  const handleClaimReward = (symbol) => {
    if (symbol === CURRENCIES.QBX) {
      setPopupState({ id: STAKING_PROCESS_STEP.REWARD_CLAIM })
    } else {
      setPopupState({ id: STAKING_PROCESS_STEP.SYMBOL_REWARD_CLAIM })
    }
  }

  const handleStakedClaim = () => {
    setPopupState({ id: STAKING_PROCESS_STEP.UN_STAKE })
  }

  return (
    <>
      <Staking
        t={t}
        symbol={tokenProfile.symbol}
        connectedWallet={connectedWallet}
        stakingProfile={stakingProfile}
        mainProfile={mainProfile}
        walletProfile={walletProfile}
        onClaimReward={handleClaimReward}
        onStakedClaim={handleStakedClaim}
        onRefreshUserTier={handleRefreshUserTier}
        stakingSymbolReward={stakingSymbolReward}
        walletConnectProps={walletConnectProps}
      />
      {renderPopup()}
      {renderWalletPopup()}
    </>
  )
}

export default StakingContainer
